import { TsDOM as q, init } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { ATTRIBUTES } from '@/scripts/core/global'

const getTsDOM = () => {
	init()
	return window.TsDOM
}

export class MenuDrawer extends UcoastEl {
	static htmlSelector = 'menu-drawer'
	static selectors = {
		element: MenuDrawer.htmlSelector,
		mainDetails: '[data-uc-drawer-main-details]',
		mainSummary: '[data-uc-drawer-main-summary]',
		subDetails: '[data-uc-drawer-sub-details]',
		subSummary: '[data-uc-drawer-sub-summary]',
		mobileAccordionDetails: '[data-uc-mobile-accordion]',
		mobileAccordionSummary: '[data-uc-mobile-accordion-summary]',
		submenu: `[${ATTRIBUTES.submenu}]`,
		submenuOpen: `[${ATTRIBUTES.submenu}="open"]`,
		applyButton: `[data-uc-facet-apply]`,
		desktopInner: '.menu-drawer__desktop-inner',
		desktopHoverTarget: '.menu-drawer__desktop-link, .menu-drawer__featured-card',
	}
	instanceSelectors = MenuDrawer.selectors
	mainDetails: HTMLDetailsElement
	mainSummary: HTMLElement
	desktopInner?: HTMLElement
	activeDesktopHoverTarget: HTMLElement | null = null
	desktopHoverClearTimeout?: number
	subDetails?: NodeListOf<HTMLDetailsElement>
	subSummaries?: NodeListOf<HTMLElement>
	mobileAccordionDetails?: NodeListOf<HTMLDetailsElement>
	mobileAccordionSummaries?: NodeListOf<HTMLElement>
	applyButtons?: NodeListOf<HTMLButtonElement>
	constructor() {
		super()
		init()
		this.getInstanceSelectors()
		this.mainDetails = q.rs(this.instanceSelectors.mainDetails, this)
		this.mainSummary = q.rs(this.instanceSelectors.mainSummary, this)
		this.desktopInner = q.os(this.instanceSelectors.desktopInner, this)
		this.subDetails = q.ol(this.instanceSelectors.subDetails, this)
		this.subSummaries = q.ol(this.instanceSelectors.subSummary, this)
		this.mobileAccordionDetails = q.ol(
			this.instanceSelectors.mobileAccordionDetails,
			this
		)
		this.mobileAccordionSummaries = q.ol(
			this.instanceSelectors.mobileAccordionSummary,
			this
		)
		// apply filters buttons
		this.applyButtons = q.ol(this.instanceSelectors.applyButton, this)
		this.addEventListener('keyup', this.onKeyUp.bind(this))
		this.addEventListener('focusout', this.onFocusOut.bind(this))
		this.bindEvents()
	}

	onReload() {
		this.mainDetails = q.rs(this.instanceSelectors.mainDetails, this)
		this.mainSummary = q.rs(this.instanceSelectors.mainSummary, this)
		this.desktopInner = q.os(this.instanceSelectors.desktopInner, this)
		this.subDetails = q.ol(this.instanceSelectors.subDetails, this)
		this.subSummaries = q.ol(this.instanceSelectors.subSummary, this)
		this.mobileAccordionDetails = q.ol(
			this.instanceSelectors.mobileAccordionDetails,
			this
		)
		this.mobileAccordionSummaries = q.ol(
			this.instanceSelectors.mobileAccordionSummary,
			this
		)
		this.applyButtons = q.ol(this.instanceSelectors.applyButton, this)
		this.bindEvents()
	}

	getInstanceSelectors() {
		this.instanceSelectors = MenuDrawer.selectors
	}

	bindEvents() {
		this.mainSummary.addEventListener('click', this.onMainSummaryClick.bind(this))
		this.subSummaries?.forEach((summary) =>
			summary.addEventListener('click', this.onSubSummaryClick.bind(this))
		)
		this.mobileAccordionSummaries?.forEach((summary) =>
			summary.addEventListener('click', this.onMobileAccordionSummaryClick.bind(this))
		)
		this.applyButtons?.forEach((button) =>
			button.addEventListener('click', this.onApplyButtonClick.bind(this))
		)
		this.querySelectorAll('.menu-drawer__close-button, .mobile-facets__close-button').forEach(
			(button) => button.addEventListener('click', this.onCloseButtonClick.bind(this))
		)
		this.bindDesktopHoverInteractions()
	}

	getDesktopHoverTarget(eventTarget: EventTarget | null) {
		if (!(eventTarget instanceof Element)) return null
		return eventTarget.closest<HTMLElement>(this.instanceSelectors.desktopHoverTarget)
	}

	clearDesktopHoverClearTimeout() {
		if (typeof this.desktopHoverClearTimeout !== 'number') return
		window.clearTimeout(this.desktopHoverClearTimeout)
		this.desktopHoverClearTimeout = undefined
	}

	scheduleDesktopHoverClear() {
		this.clearDesktopHoverClearTimeout()
		this.desktopHoverClearTimeout = window.setTimeout(() => {
			this.setDesktopHoverState(null)
		}, 90)
	}

	setDesktopHoverState(target: HTMLElement | null) {
		if (!this.desktopInner) return
		this.clearDesktopHoverClearTimeout()
		if (!target) {
			this.activeDesktopHoverTarget?.classList.remove('is-hover-target')
			this.activeDesktopHoverTarget = null
			this.desktopInner.classList.remove('is-hover-active')
			return
		}
		if (this.activeDesktopHoverTarget === target) return
		this.activeDesktopHoverTarget?.classList.remove('is-hover-target')
		this.desktopInner.classList.add('is-hover-active')
		target.classList.add('is-hover-target')
		this.activeDesktopHoverTarget = target
	}

	onDesktopPointerMove = (event: PointerEvent) => {
		if (event.pointerType === 'touch') return
		const target = this.getDesktopHoverTarget(event.target)
		target ? this.setDesktopHoverState(target) : this.scheduleDesktopHoverClear()
	}

	onDesktopPointerLeave = () => {
		this.scheduleDesktopHoverClear()
	}

	onDesktopFocusIn = (event: FocusEvent) => {
		this.setDesktopHoverState(this.getDesktopHoverTarget(event.target))
	}

	onDesktopFocusOut = () => {
		window.setTimeout(() => {
			const target = this.getDesktopHoverTarget(document.activeElement)
			target ? this.setDesktopHoverState(target) : this.scheduleDesktopHoverClear()
		}, 0)
	}

	bindDesktopHoverInteractions() {
		if (!this.desktopInner || this.desktopInner.dataset.ucHoverBound === 'true') return
		this.desktopInner.dataset.ucHoverBound = 'true'
		this.desktopInner.addEventListener('pointermove', this.onDesktopPointerMove)
		this.desktopInner.addEventListener('pointerleave', this.onDesktopPointerLeave)
		this.desktopInner.addEventListener('focusin', this.onDesktopFocusIn)
		this.desktopInner.addEventListener('focusout', this.onDesktopFocusOut)
	}

	isHeaderDrawer() {
		return this.matches('header-drawer')
	}

	onApplyButtonClick(event: MouseEvent) {
		event.preventDefault()
		this.mainSummary.click()
	}

	onKeyUp(event: KeyboardEvent) {
		if (event.code?.toUpperCase() !== 'ESCAPE') return

		const openDetailsElement = q.oClosestTarget(event, 'details[open]')
		if (!openDetailsElement) return

		openDetailsElement === this.mainDetails
			? this.closeMenuDrawer(event, this.mainSummary)
			: this.closeSubmenu(openDetailsElement)
	}

	onAllSummaryClick(event: MouseEvent) {
		const summaryElement = q.rct(event)
		const detailsElement = summaryElement.parentNode
		if (!(detailsElement instanceof HTMLElement)) throw new Error('detailsElement is null')
		const parentMenuElement = q.oc(detailsElement, this.instanceSelectors.submenu)
		const isOpen = detailsElement.hasAttribute('open')
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

		function addTrapFocus() {
			const nextElementSibling = summaryElement.nextElementSibling
			if (!(detailsElement instanceof HTMLElement))
				throw new Error('cannot addTrapFocus, detailsElement is null')
			if (!(nextElementSibling instanceof HTMLElement))
				throw new Error('cannot addTrapFocus, nextElementSibling is null')

			getTsDOM()?.trapFocus(nextElementSibling, q.os('button', detailsElement))
			nextElementSibling.removeEventListener('transitionend', addTrapFocus)
		}
		return {
			detailsElement,
			summaryElement,
			parentMenuElement,
			isOpen,
			reducedMotion,
			addTrapFocus,
		}
	}

	onMainSummaryClick(event: MouseEvent) {
		const { isOpen, summaryElement } = this.onAllSummaryClick(event)
		if (isOpen) event.preventDefault()
		isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement)

		if (window.matchMedia('(max-width: 990px)').matches) {
			document.documentElement.style.setProperty(
				'--viewport-height',
				`${window.innerHeight}px`
			)
		}
	}

	onSubSummaryClick(event: MouseEvent) {
		const { parentMenuElement, reducedMotion, addTrapFocus, detailsElement, summaryElement } =
			this.onAllSummaryClick(event)
		setTimeout(() => {
			detailsElement.setAttribute(ATTRIBUTES.menuOpening, '')
			summaryElement.setAttribute('aria-expanded', 'true')
			parentMenuElement && parentMenuElement.setAttribute(ATTRIBUTES.submenu, 'open')
			!reducedMotion || reducedMotion.matches
				? addTrapFocus()
				: summaryElement.nextElementSibling?.addEventListener('transitionend', addTrapFocus)
		}, 100)
	}

	openMenuDrawer(summaryElement: HTMLElement) {
		if (this.isHeaderDrawer()) {
			const activeCartDrawer = q.os<HTMLElement>('cart-drawer.drawer.active')
			if (activeCartDrawer && typeof (activeCartDrawer as { close?: () => void }).close === 'function') {
				;(activeCartDrawer as { close: () => void }).close()
			}
		}
		setTimeout(() => {
			this.mainDetails.setAttribute(ATTRIBUTES.menuOpening, '')
		})
		summaryElement.setAttribute('aria-expanded', 'true')
		getTsDOM()?.trapFocus(this.mainDetails, summaryElement)
		document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`)
		this.setDesktopHoverState(null)
		this.syncDefaultMobileAccordion()
	}

	closeMenuDrawer(event: Event | undefined, elementToFocus: HTMLElement | undefined = undefined) {
		this.mainDetails.removeAttribute(ATTRIBUTES.menuOpening)
		this.mainDetails.querySelectorAll('details').forEach((details) => {
			details.removeAttribute('open')
			details.removeAttribute(ATTRIBUTES.menuOpening)
		})
		this.mainDetails.querySelectorAll(this.instanceSelectors.submenu).forEach((submenu) => {
			submenu.setAttribute(ATTRIBUTES.submenu, 'closed')
		})
		document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`)
		this.setDesktopHoverState(null)
		getTsDOM()?.removeTrapFocus(elementToFocus)
		this.closeAnimation(this.mainDetails)

		if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', 'false')
	}

	onFocusOut(event: Event) {
		setTimeout(() => {
			if (
				this.mainDetails.hasAttribute('open') &&
				!this.mainDetails.contains(document.activeElement)
			)
				this.closeMenuDrawer(event)
		})
	}

	onCloseButtonClick(event: MouseEvent) {
		const detailsElement = q.oClosestTarget(event, 'details')
		if (!detailsElement) {
			this.closeMenuDrawer(event, this.mainSummary)
			return
		}
		if (detailsElement === this.mainDetails) {
			this.closeMenuDrawer(event, this.mainSummary)
			return
		}
		this.closeSubmenu(detailsElement)
	}

	closeSubmenu(detailsElement: HTMLElement) {
		const parentMenuElement = detailsElement.closest(this.instanceSelectors.submenuOpen)
		parentMenuElement && parentMenuElement.setAttribute(ATTRIBUTES.submenu, 'closed')
		detailsElement.removeAttribute(ATTRIBUTES.menuOpening)
		const summaryElement = q.rs('summary', detailsElement)
		summaryElement.setAttribute('aria-expanded', 'false')
		getTsDOM()?.removeTrapFocus(summaryElement)
		this.closeAnimation(detailsElement)
	}

	closeAnimation(detailsElement: HTMLElement) {
		let animationStart: number | undefined

		const handleAnimation = (time: number) => {
			if (animationStart === undefined) {
				animationStart = time
			}

			const elapsedTime = time - animationStart

			if (elapsedTime < 400) {
				window.requestAnimationFrame(handleAnimation)
			} else {
				detailsElement.removeAttribute('open')
				const closestDetails = q.oc(detailsElement, 'details[open]')
				if (closestDetails) {
					const summaryElement = q.rs('summary', detailsElement)
					getTsDOM()?.trapFocus(closestDetails, summaryElement)
				}
			}
		}

		window.requestAnimationFrame(handleAnimation)
	}

	onMobileAccordionSummaryClick(event: MouseEvent) {
		if (!this.isHeaderDrawer()) return
		const summaryElement = q.rct(event)
		const detailsElement = q.oc<HTMLDetailsElement>(
			summaryElement,
			this.instanceSelectors.mobileAccordionDetails
		)
		if (!detailsElement) return

		window.setTimeout(() => {
			if (!detailsElement.hasAttribute('open')) return
			this.mobileAccordionDetails?.forEach((item) => {
				if (item !== detailsElement) item.removeAttribute('open')
			})
		}, 0)
	}

	syncDefaultMobileAccordion() {
		if (!this.isHeaderDrawer()) return
		const accordionItems = this.mobileAccordionDetails
		if (!accordionItems || !accordionItems.length) return

		let hasOpen = false
		accordionItems.forEach((item) => {
			if (item.hasAttribute('open')) hasOpen = true
		})
		if (hasOpen) return
	}
}
